"use client";

import { useEffect } from "react";
import { buildTitle, buildCanonical, siteMeta } from "./seo";

interface UsePageMetadataOptions {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
}

/**
 * Hook to dynamically update page metadata for client components
 * Updates document title, meta description, OpenGraph tags, and canonical URL
 */
export function usePageMetadata({
  title,
  description,
  path,
  image,
}: UsePageMetadataOptions) {
  useEffect(() => {
    // Check if document is available
    if (typeof document === 'undefined' || !document.head) {
      return;
    }

    const pageTitle = title ? buildTitle(title) : siteMeta.defaultTitle;
    const pageDescription = description || siteMeta.defaultDescription;
    const canonical = path ? buildCanonical(path) : siteMeta.baseUrl;
    const ogImage = image || "https://pacificnorthnorth-images-noprod.s3.us-east-1.amazonaws.com/assets/og-pacificnorthnorth.png";

    // Update document title
    document.title = pageTitle;

    // Remove any existing meta description tags to avoid duplicates
    // Note: We don't actually need to remove them - we can just update the first one
    // This avoids any potential removeChild errors during navigation
    const existingDescriptions = document.querySelectorAll('meta[name="description"]');
    if (existingDescriptions.length > 0) {
      // Update the first one instead of removing and recreating
      const firstDesc = existingDescriptions[0] as HTMLMetaElement;
      if (firstDesc) {
        firstDesc.setAttribute("content", pageDescription);
      }
      // Remove any additional duplicates (but only if they still have a parent)
      for (let i = 1; i < existingDescriptions.length; i++) {
        const tag = existingDescriptions[i];
        try {
          const parent = tag.parentNode;
          if (parent && parent === document.head) {
            parent.removeChild(tag);
          }
        } catch (e) {
          // Element may have been removed already - safe to ignore
          console.debug("Could not remove duplicate meta description tag:", e);
        }
      }
    }

    // Create a single meta description tag (only if we didn't update an existing one)
    if (existingDescriptions.length === 0) {
      const metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      metaDescription.setAttribute("content", pageDescription);
      if (document.head) {
        document.head.appendChild(metaDescription);
      }
    }

    // Remove any existing canonical links to avoid duplicates
    // Note: We can update the first one instead of removing and recreating
    const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
    if (existingCanonicals.length > 0) {
      // Update the first one instead of removing and recreating
      const firstCanonical = existingCanonicals[0] as HTMLLinkElement;
      if (firstCanonical) {
        firstCanonical.setAttribute("href", canonical);
      }
      // Remove any additional duplicates (but only if they still have a parent)
      for (let i = 1; i < existingCanonicals.length; i++) {
        const tag = existingCanonicals[i];
        try {
          const parent = tag.parentNode;
          if (parent && parent === document.head) {
            parent.removeChild(tag);
          }
        } catch (e) {
          // Element may have been removed already - safe to ignore
          console.debug("Could not remove duplicate canonical link:", e);
        }
      }
    }

    // Create a single canonical link (only if we didn't update an existing one)
    if (existingCanonicals.length === 0) {
      const canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      canonicalLink.setAttribute("href", canonical);
      if (document.head) {
        document.head.appendChild(canonicalLink);
      }
    }

    // Update OpenGraph tags
    const updateOGTag = (property: string, content: string) => {
      if (!document.head) return;
      try {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("property", property);
          if (document.head) {
            document.head.appendChild(tag);
          }
        }
        if (tag) {
          tag.setAttribute("content", content);
        }
      } catch (e) {
        console.debug("Could not update OG tag:", property, e);
      }
    };

    updateOGTag("og:title", pageTitle);
    updateOGTag("og:description", pageDescription);
    updateOGTag("og:url", canonical);
    updateOGTag("og:image", ogImage);
    updateOGTag("og:type", "website");
    updateOGTag("og:site_name", siteMeta.siteName);

    // Update Twitter card tags
    const updateTwitterTag = (name: string, content: string) => {
      if (!document.head) return;
      try {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("name", name);
          if (document.head) {
            document.head.appendChild(tag);
          }
        }
        if (tag) {
          tag.setAttribute("content", content);
        }
      } catch (e) {
        console.debug("Could not update Twitter tag:", name, e);
      }
    };

    updateTwitterTag("twitter:card", "summary_large_image");
    updateTwitterTag("twitter:title", pageTitle);
    updateTwitterTag("twitter:description", pageDescription);
    updateTwitterTag("twitter:image", ogImage);

    // No cleanup needed - Next.js and the browser handle metadata cleanup automatically
    // Attempting to manually clean up can cause errors during navigation
  }, [title, description, path, image]);
}

